import { PlatformID } from '@common/clients/api';
import { type ResolvedContextData } from '@common/defaults/ContextData';
import { type SidebarWidgetProps, WidgetType } from '@common/types/Sidebar';
import { getT, Ns } from '@web/i18n';
import { Tabs } from '@web/molecules/Tabs';
import { Widget } from '@web/molecules/Widget';
import { WidgetHeader } from '@web/molecules/Widget/atoms/WidgetHeader';
import { WidgetHeaderSection } from '@web/molecules/Widget/atoms/WidgetHeaderSection';
import { WidgetTitle } from '@web/molecules/Widget/atoms/WidgetTitle';

import { Widget as MostCommented } from '../MostCommented';
import { Widget as MostRead } from '../MostRead';
import { Widget as ProNews } from '../ProNews';
import { getNewsPopularThemeConfig } from './NewsPopular.config';

interface Props {
    contextData: ResolvedContextData;
    dataList?: SidebarWidgetProps;
}

export const NewsPopular = ({ contextData, dataList }: Props) => {
    const { context, platform } = contextData;
    const __sidebar = getT(context.locale, Ns.Sidebar);

    const themeConfig = getNewsPopularThemeConfig(contextData);
    const platformID = context?.platform || PlatformID.VP;
    const platformName = themeConfig.usePlatformFullNameInHeading ? platform.name : platformID.toUpperCase();

    const config = themeConfig.tabbedWidgetConfig;
    const heading = __sidebar(config.title, { platform: platformName });
    const hasTabs = config.tabs.length > 1;

    const tabs = config.tabs.map((tab) => {
        let content;

        switch (tab.type) {
            case WidgetType.MOST_READ:
                content = (
                    <MostRead
                        contextData={contextData}
                        dataList={dataList ? dataList[WidgetType.MOST_READ] : []}
                        isTabbedWidget
                    />
                );
                break;
            case WidgetType.MOST_COMMENTED:
                content = (
                    <MostCommented
                        contextData={contextData}
                        dataList={dataList ? dataList[WidgetType.MOST_COMMENTED] : []}
                        isTabbedWidget
                    />
                );
                break;
            case WidgetType.PRO_NEWS:
                content = (
                    <ProNews
                        contextData={contextData}
                        dataList={dataList ? dataList[WidgetType.PRO_NEWS] : []}
                        isTabbedWidget
                    />
                );
                break;
        }

        return {
            title: tab.icon ? tab.icon(contextData) : __sidebar(tab.title),
            content,
        };
    });

    return (
        <Widget title={heading}>
            <WidgetHeader>
                <WidgetHeaderSection noBorderBottom={hasTabs}>
                    <WidgetTitle
                        contextData={contextData}
                        hideBorderBottomUnderline={
                            hasTabs ? themeConfig.hideTitleUnderlineWhenToggle : undefined
                        }
                    >
                        {heading}
                    </WidgetTitle>
                </WidgetHeaderSection>
                {hasTabs ? <Tabs tabs={tabs} disablePathRouting noMargin /> : tabs[0]?.content}
            </WidgetHeader>
        </Widget>
    );
};
